import React from 'react';
import { Card } from 'primereact/card';
import Dumbbell from './icons/Dumbbell';
import Padlock from './icons/Padlock';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { ArrowRight } from 'lucide-react';
import Pencil from './icons/Pencil';
import { useNavigate } from 'react-router-dom';
import CheckLime from './icons/CheckLime';
import IceCube from './icons/IceCube';
import { useUserActionMutation } from '../../../custom-hooks/useUserActionMutation';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { Tooltip } from 'primereact/tooltip';
import Rings from './icons/Rings';
import { useClassFocusOnboarding } from '../../../contexts/ClassFocusOnboardingContext';

interface Props {
  widgetType: DiagnosticWidgetType;
  checked: boolean;
  blocked: boolean;
  /** Records the task as done for the rest of the session — see `locallyCompleted` in widgetHelper. */
  onWidgetCompleted: (widgetType: DiagnosticWidgetType) => void;
}

export enum DiagnosticWidgetType {
  REVIEW_CLASSES,
  PREPARE,
  UNDERSTAND_STUDENT_EXPERIENCE,
  CLASS_FOCUS,
}

const widgetConfig = {
  [DiagnosticWidgetType.REVIEW_CLASSES]: {
    icon: <Pencil />,
    title: 'Review your classes',
    titleCompleted: 'Class reviewed',
    description: 'Make sure your classes are set up correctly',
    buttonLabel: 'Take a look',
    buttonClick: '/classes-rosters',
    linked: false,
    ariaLabel: 'checkbox-review',
    userActionId: 'ActionTypeNode:17',
    userActionDescription: 'Review Classes Completed',
    tooltipMessage: 'This task will unlock once your classes and students are ready',
  },
  [DiagnosticWidgetType.PREPARE]: {
    icon: <Dumbbell />,
    title: 'Prepare for the diagnostic',
    titleCompleted: 'Diagnostic prep completed',
    description: 'See what it covers and how to introduce it to your class',
    buttonLabel: 'Prepare now',
    buttonClick: '/prep-diagnostics',
    linked: true,
    ariaLabel: 'checkbox-prepare',
    userActionId: 'ActionTypeNode:18',
    userActionDescription: 'Prepare for Diagnostic Completed',
    tooltipMessage: '',
  },
  [DiagnosticWidgetType.UNDERSTAND_STUDENT_EXPERIENCE]: {
    icon: <IceCube />,
    title: 'Understand the student experience',
    titleCompleted: 'Student experience explored',
    description: 'Learn about and interact with what your students will see',
    buttonLabel: 'Start exploring',
    buttonClick: '/how-students-learn',
    linked: true,
    ariaLabel: 'checkbox-expectations',
    userActionId: 'ActionTypeNode:19',
    userActionDescription: 'Understand Student Experience Completed',
    tooltipMessage: '',
  },
  [DiagnosticWidgetType.CLASS_FOCUS]: {
    icon: <Rings />,
    title: 'Set a class focus',
    titleCompleted: 'Focus activated',
    description: 'Select the starting unit for Roadmaps to align with the content you’re teaching.',
    buttonLabel: 'Set a focus',
    // Handled in-page (scroll + pulse) rather than navigating — see handleActionClick.
    buttonClick: '',
    linked: true,
    ariaLabel: 'checkbox-class-focus',
    userActionId: 'ActionTypeNode:20',
    userActionDescription: 'Set Class Focus Completed',
    tooltipMessage: 'This task unlocks once students start finishing their diagnostic',
  },
};

export const TeacherDiagnosticWidget: React.FC<Props> = ({ widgetType, checked, blocked, onWidgetCompleted }) => {
  const navigate = useNavigate();
  const userRedux = useAppSelector(selectUser);
  const { requestFocusAttention } = useClassFocusOnboarding();

  const config = widgetConfig[widgetType];
  const [commitMutation] = useUserActionMutation();

  const handleActionClick = () => {
    // Class focus lives further down the same page: scroll to it and pulse instead of navigating.
    if (widgetType === DiagnosticWidgetType.CLASS_FOCUS) {
      requestFocusAttention();
      return;
    }
    // The SPA keeps the dashboard's scroll offset across routes: reset it so the target page opens at the top.
    // Only the Review classes step should light up the first class card once the roster page loads;
    // other entry paths (header menu, direct URL) navigate without this flag so nothing pulses.
    window.scrollTo({ top: 0, behavior: 'instant' });
    navigate(
      config.buttonClick,
      widgetType === DiagnosticWidgetType.REVIEW_CLASSES ? { state: { highlightFirstClass: true } } : undefined,
    );
  };

  const renderHeader = () => (
    <div className={`tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-x-3 `}>
      <div className="tw-flex tw-items-center tw-gap-x-2">
        {blocked ? <Padlock /> : checked ? <CheckLime /> : config.icon}

        {(checked || blocked) && (
          <span className={`tw-font-atkinson tw-font-bold tw-text-sm ${blocked ? 'tw-text-neutral-800' : ''}`}>
            {checked ? config.titleCompleted : config.title}
          </span>
        )}
      </div>

      {!blocked && (
        <Checkbox
          className="nc_teacher-diagnostic-widget-checkbox"
          checked={checked}
          disabled={checked}
          aria-label={config.ariaLabel}
          aria-labelledby={config.ariaLabel}
          onChange={e => {
            e.originalEvent?.stopPropagation();
            // The checkbox is disabled once checked, so this only ever means "mark as done".
            if (e.checked) onWidgetCompleted(widgetType);
          }}
          onClick={e => {
            e.stopPropagation();
            commitMutation({
              variables: {
                input: {
                  userAction: {
                    userId: `UserNode:${userRedux?.id}`,
                    actionTypeId: config.userActionId,
                    timeStamp: new Date().toISOString(),
                    description: config.userActionDescription,
                  },
                },
              },
            });
          }}
          tooltip={!checked ? 'Mark this as done' : undefined}
          tooltipOptions={{ position: 'top', className: '!tw-font-atkinson' }}
        />
      )}
    </div>
  );

  const content = () => {
    return (
      <div className="tw-flex tw-flex-col tw-gap-y-2">
        <h1 className="tw-text-base tw-font-atkinson tw-font-bold tw-text-neutral-950">{config.title}</h1>
        <p className="tw-text-sm tw-font-atkinson tw-text-neutral-700 tw-m-0">{config.description}</p>
        <Button
          label={config.buttonLabel}
          icon={<ArrowRight size={16} />}
          className="tw-mt-3 tw-gap-x-2 tw-self-start tw-flex tw-flex-row-reverse"
          link={config.linked}
          onClick={handleActionClick}
        />
      </div>
    );
  };

  return (
    <>
      {blocked && <Tooltip target={'.nc_teacher-diagnostic-widget'} className="!tw-font-atkinson !tw-w-[172px]" />}
      <Card
        pt={{
          title: {
            className: `!tw-border-none ${checked ? '!tw-pb-0' : ''}`,
          },
          content: { className: '!tw-pt-1 !tw-items-start' },
        }}
        className={`nc_teacher-diagnostic-widget ${blocked ? 'blocked' : ''} ${
          checked ? '!tw-border-lime-500 !tw-border-b-4' : ''
        }`}
        title={renderHeader}
        onClick={() => {
          if (!blocked) handleActionClick();
        }}
        data-pr-tooltip={blocked ? config.tooltipMessage : undefined}
        data-pr-position="left"
      >
        {!checked && !blocked && <div className="tw-widget-content tw-flex tw-items-start">{content()}</div>}
      </Card>
    </>
  );
};

export default TeacherDiagnosticWidget;
